import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Link,
  Grid,
  Alert,
  AlertTitle,
  CircularProgress,
  Stack,
  useTheme,
  Modal,
  Tooltip,
  IconButton,
  LinearProgress,
  useMediaQuery,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Link as LinkIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  ContactPage as ContactPageIcon,
  Phone as PhoneIcon,
  Refresh as RefreshIcon,
  Description as DescriptionIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  LocationOn as LocationOnIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Close as XIcon
} from '@mui/icons-material';
import { LeadData, LeadFormData } from '../types';
import { useState } from 'react';
import { keyframes } from '@mui/system';

// Define the spin animation
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

interface ResultsDisplayProps {
  results: LeadData[];
  isComplete: boolean;
  message?: string;
  error?: string;
  suggestions?: string[];
  onFindContacts?: (businessIndex: number) => void;
  onReloadBusiness?: (businessIndex: number) => void;
  onLoadMore?: () => Promise<LeadData[] | undefined>;
  isLoadingMore?: boolean;
  formData?: LeadFormData | null;
  noMoreResults?: boolean;
}

const ResultsDisplay = ({
  results,
  isComplete,
  message,
  error,
  suggestions,
  onFindContacts,
  onReloadBusiness,
  onLoadMore,
  isLoadingMore = false,
  formData,
  noMoreResults
}: ResultsDisplayProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [bioModalOpen, setBioModalOpen] = useState(false);
  const [selectedBio, setSelectedBio] = useState<{ name: string; bio: string } | null>(null);
  const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>({});
  const [compactView, setCompactView] = useState(true);
  const [aboutUsModalOpen, setAboutUsModalOpen] = useState(false);
  const [selectedAboutUs, setSelectedAboutUs] = useState<{ name: string; aboutUs: string } | null>(null);

  const handleOpenBioModal = (name: string, bio: string) => {
    setSelectedBio({ name, bio });
    setBioModalOpen(true);
  };

  const handleCloseBioModal = () => {
    setBioModalOpen(false);
    setSelectedBio(null);
  };

  const handleOpenAboutUsModal = (name: string, aboutUs: string) => {
    setSelectedAboutUs({ name, aboutUs });
    setAboutUsModalOpen(true);
  };

  const handleCloseAboutUsModal = () => {
    setAboutUsModalOpen(false);
    setSelectedAboutUs(null);
  };

  const toggleCardExpansion = (index: number) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const toggleViewMode = () => {
    setCompactView(!compactView);
    // Reset expanded state when switching view modes
    setExpandedCards({});
  };

  // Function to determine the appropriate icon for a social link
  const getSocialIcon = (url: string) => {
    const hostname = new URL(url).hostname.toLowerCase();

    if (hostname.includes('facebook') || hostname.includes('fb.com')) {
      return <FacebookIcon sx={{ display: 'flex' }} />;
    } else if (hostname.includes('twitter') || hostname.includes('x.com')) {
      return <XIcon sx={{ display: 'flex'}} />;
    } else if (hostname.includes('linkedin')) {
      return <LinkedInIcon sx={{ display: 'flex' }} />;
    } else if (hostname.includes('instagram')) {
      return <InstagramIcon sx={{ display: 'flex' }} />;
    } else if (hostname.includes('google') && hostname.includes('maps')) {
      return <LocationOnIcon sx={{ display: 'flex' }} />;
    } else {
      return <LinkIcon sx={{ display: 'flex' }} />;
    }
  };

  // Function to get a friendly name for the social link
  const getSocialName = (url: string) => {
    const hostname = new URL(url).hostname.toLowerCase();

    if (hostname.includes('facebook') || hostname.includes('fb.com')) {
      return 'Facebook';
    } else if (hostname.includes('twitter') || hostname.includes('x.com')) {
      return 'X';
    } else if (hostname.includes('linkedin')) {
      return 'LinkedIn';
    } else if (hostname.includes('instagram')) {
      return 'Instagram';
    } else if (hostname.includes('google') && hostname.includes('maps')) {
      return 'Google Maps';
    } else {
      return hostname.replace('www.', '');
    }
  };

  // Common style for all contact chips to ensure consistent sizing
  const contactChipStyle = {
    minWidth: '136px',
    width: '136px',
    height: '32px',
    fontWeight: 500,
    fontSize: '0.8125rem',
    '& .MuiChip-icon': {
      marginLeft: '8px'
    },
    '& .MuiChip-label': {
      padding: '0 12px'
    }
  };

  if (!isComplete) {
    return null;
  }

  // If there are no results but we have a message, display it
  if (results.length === 0 && (message || error)) {
    return (
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Alert
          severity={error ? "error" : "info"}
          icon={error ? <ErrorIcon /> : <InfoIcon />}
          sx={{ mb: 3 }}
        >
          <AlertTitle>{error ? "Error" : "No Results Found"}</AlertTitle>
          {message || error}
        </Alert>

        {suggestions && suggestions.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Suggestions:
            </Typography>
            <List>
              {suggestions.map((suggestion, index) => (
                <ListItem key={index}>
                  <ListItemText primary={suggestion} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Paper>
    );
  }

  return (
    <>
      <Paper elevation={3} sx={{
        p: isMobile ? 2 : 4, 
        mt: 4, 
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Typography variant={isMobile ? "h6" : "h5"} component="h2">
            Results ({results.length} businesses found)
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={compactView}
                onChange={toggleViewMode}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {compactView ? <ViewListIcon fontSize="small" /> : <ViewModuleIcon fontSize="small" />}
                <Typography variant="body2">
                  {compactView ? "Compact View" : "Full View"}
                </Typography>
              </Box>
            }
          />
        </Box>

        <Grid container spacing={compactView ? (isMobile ? 1 : 1.5) : (isMobile ? 2 : 3)}>
          {results.map((business, index) => (
            <Grid item xs={12} key={index}>
              {compactView ? (
                // Compact View
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    overflow: 'hidden',
                    mb: compactView ? 0 : 1
                  }}
                >
                  <CardContent sx={{ 
                    p: compactView ? 1.5 : 2, 
                    '&:last-child': { pb: compactView ? 1.5 : 2 },
                    transition: 'all 0.2s'
                  }}>
                    {isMobile ? (
                      // Mobile layout - 4 rows (updated from 3)
                      <>
                        {/* Row 1: Business name */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                          {/* Relevancy score circle (mobile) */}
                          <Box sx={{ 
                            width: 36, 
                            height: 36, 
                            borderRadius: '50%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            bgcolor: business.relevancyScore !== undefined 
                              ? (business.relevancyScore > 70 ? 'rgba(76, 175, 80, 0.15)' : 
                                business.relevancyScore > 40 ? 'rgba(255, 152, 0, 0.15)' : 'rgba(244, 67, 54, 0.15)')
                              : 'rgba(0, 0, 0, 0.05)',
                            color: business.relevancyScore !== undefined
                              ? (business.relevancyScore > 70 ? 'success.dark' : 
                                business.relevancyScore > 40 ? 'warning.dark' : 'error.dark')
                              : 'text.secondary',
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            flexShrink: 0,
                            border: 1,
                            borderColor: business.relevancyScore !== undefined
                              ? (business.relevancyScore > 70 ? 'rgba(76, 175, 80, 0.3)' : 
                                business.relevancyScore > 40 ? 'rgba(255, 152, 0, 0.3)' : 'rgba(244, 67, 54, 0.3)')
                              : 'rgba(0, 0, 0, 0.1)'
                          }}>
                            {business.relevancyScore !== undefined ? `${business.relevancyScore}%` : '—'}
                          </Box>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              color: 'rgba(33, 150, 243, 0.8)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              fontSize: '0.95rem'
                            }}
                          >
                            {business.name}
                          </Typography>
                        </Box>
                        
                        {/* Row 2: Address */}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            mb: 1
                          }}
                        >
                          {business.address}
                        </Typography>
                        
                        {/* Row 3: Contact info icons */}
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1,
                          flexWrap: 'wrap',
                          mb: 1
                        }}>
                          {business.website && (
                            <Tooltip title="Website">
                              <IconButton
                                size="small"
                                component="a"
                                href={business.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                color="primary"
                              >
                                <LinkIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {(business as { email?: string }).email && (
                            <Tooltip title={(business as { email?: string }).email}>
                              <IconButton
                                size="small"
                                component="a"
                                href={`mailto:${(business as { email?: string }).email}`}
                                color="primary"
                              >
                                <EmailIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {business.phone && (
                            <Tooltip title={business.phone}>
                              <IconButton
                                size="small"
                                component="a"
                                href={`tel:${business.phone}`}
                                color="secondary"
                              >
                                <PhoneIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {business.businessSocialLinks?.slice(0, 2).map((link, i) => (
                            <Tooltip key={i} title={getSocialName(link)}>
                              <IconButton
                                size="small"
                                component="a"
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                {getSocialIcon(link)}
                              </IconButton>
                            </Tooltip>
                          ))}
                          
                          {business.businessSocialLinks && business.businessSocialLinks.length > 2 && (
                            <Tooltip title={`${business.businessSocialLinks.length - 2} more links`}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                bgcolor: 'rgba(0, 0, 0, 0.04)',
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                                fontWeight: 'bold'
                              }}>
                                +{business.businessSocialLinks.length - 2}
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                        
                        {/* Row 4: Contacts and expand button */}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%'
                        }}>
                          {/* Contacts chip */}
                          <Box sx={{ flex: '1 1 auto' }}>
                            {business.teamMembers && business.teamMembers.length > 0 ? (
                              <Chip
                                icon={<PersonIcon />}
                                label={`${business.teamMembers.length} contact${business.teamMembers.length !== 1 ? 's' : ''}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ 
                                  ...contactChipStyle
                                }}
                              />
                            ) : business.isLoadingContacts ? (
                              <Chip
                                icon={<CircularProgress size={16} />}
                                label="Loading..."
                                size="small"
                                color="default"
                                variant="outlined"
                                sx={{ 
                                  ...contactChipStyle
                                }}
                              />
                            ) : (
                              <Chip
                                icon={<ContactPageIcon />}
                                label="Find Contacts"
                                size="small"
                                color="primary"
                                onClick={() => onFindContacts && onFindContacts(index)}
                                disabled={!onFindContacts}
                                sx={{ 
                                  ...contactChipStyle,
                                  bgcolor: theme.palette.primary.main,
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: theme.palette.primary.dark,
                                  }
                                }}
                              />
                            )}
                          </Box>
                          
                          {/* Expand/collapse button */}
                          <Box sx={{ flex: '0 0 auto' }}>
                            <Tooltip title={expandedCards[index] ? "Collapse" : "Expand"}>
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => toggleCardExpansion(index)}
                                sx={{
                                  ml: 1,
                                  bgcolor: 'rgba(33, 150, 243, 0.08)',
                                  '&:hover': {
                                    bgcolor: 'rgba(33, 150, 243, 0.15)',
                                  }
                                }}
                              >
                                {expandedCards[index] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </>
                    ) : (
                      // Desktop layout - original horizontal layout
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        width: '100%'
                      }}>
                        {/* Relevancy score circle (desktop) */}
                        <Box sx={{ 
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          bgcolor: business.relevancyScore !== undefined 
                            ? (business.relevancyScore > 70 ? 'rgba(76, 175, 80, 0.15)' : 
                              business.relevancyScore > 40 ? 'rgba(255, 152, 0, 0.15)' : 'rgba(244, 67, 54, 0.15)')
                            : 'rgba(0, 0, 0, 0.05)',
                          color: business.relevancyScore !== undefined
                            ? (business.relevancyScore > 70 ? 'success.dark' : 
                              business.relevancyScore > 40 ? 'warning.dark' : 'error.dark')
                            : 'text.secondary',
                          fontWeight: 'bold',
                          fontSize: '0.8rem',
                          mr: 1.5,
                          flexShrink: 0,
                          border: 1,
                          borderColor: business.relevancyScore !== undefined
                            ? (business.relevancyScore > 70 ? 'rgba(76, 175, 80, 0.3)' : 
                              business.relevancyScore > 40 ? 'rgba(255, 152, 0, 0.3)' : 'rgba(244, 67, 54, 0.3)')
                            : 'rgba(0, 0, 0, 0.1)'
                        }}>
                          {business.relevancyScore !== undefined ? `${business.relevancyScore}%` : '—'}
                        </Box>
                        
                        {/* Left section: Business name and address */}
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          flex: '1 1 40%',
                          minWidth: 0 // Needed for text truncation
                        }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              color: 'rgba(33, 150, 243, 0.8)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              fontSize: compactView ? '0.95rem' : 'inherit'
                            }}
                          >
                            {business.name}
                          </Typography>
                          
                          {/* Row 2: Address */}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              mt: compactView ? 0 : 0.5
                            }}
                          >
                            {business.address}
                          </Typography>
                        </Box>
                        
                        {/* Middle section: Contact info icons */}
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1,
                          flex: '1 1 auto',
                          justifyContent: 'flex-end',
                          flexWrap: 'wrap',
                          mr: 2
                        }}>
                          {business.website && (
                            <Tooltip title="Website">
                              <IconButton
                                size="small"
                                component="a"
                                href={business.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                color="primary"
                              >
                                <LinkIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {(business as { email?: string }).email && (
                            <Tooltip title={(business as { email?: string }).email}>
                              <IconButton
                                size="small"
                                component="a"
                                href={`mailto:${(business as { email?: string }).email}`}
                                color="primary"
                              >
                                <EmailIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {business.phone && (
                            <Tooltip title={business.phone}>
                              <IconButton
                                size="small"
                                component="a"
                                href={`tel:${business.phone}`}
                                color="secondary"
                              >
                                <PhoneIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {business.businessSocialLinks?.slice(0, 2).map((link, i) => (
                            <Tooltip key={i} title={getSocialName(link)}>
                              <IconButton
                                size="small"
                                component="a"
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                {getSocialIcon(link)}
                              </IconButton>
                            </Tooltip>
                          ))}
                          
                          {business.businessSocialLinks && business.businessSocialLinks.length > 2 && (
                            <Tooltip title={`${business.businessSocialLinks.length - 2} more links`}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                bgcolor: 'rgba(0, 0, 0, 0.04)',
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                                fontWeight: 'bold'
                              }}>
                                +{business.businessSocialLinks.length - 2}
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                        
                        {/* Right section: Contacts count and expand button */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          gap: 1,
                          flex: '0 0 auto',
                          minWidth: '160px',  // Increased to accommodate right-aligned content
                          justifyContent: 'flex-end',  // Ensure consistent alignment
                          textAlign: 'right'  // Ensure text is right-aligned
                        }}>
                          {business.teamMembers && business.teamMembers.length > 0 ? (
                            <Chip
                              icon={<PersonIcon />}
                              label={`${business.teamMembers.length} contact${business.teamMembers.length !== 1 ? 's' : ''}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ 
                                ...contactChipStyle
                              }}
                            />
                          ) : business.isLoadingContacts ? (
                            <Chip
                              icon={<CircularProgress size={16} />}
                              label="Loading..."
                              size="small"
                              color="default"
                              variant="outlined"
                              sx={{ 
                                ...contactChipStyle
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<ContactPageIcon />}
                              label="Find Contacts"
                              size="small"
                              color="primary"
                              onClick={() => onFindContacts && onFindContacts(index)}
                              disabled={!onFindContacts}
                              sx={{ 
                                ...contactChipStyle,
                                bgcolor: theme.palette.primary.main,
                                color: 'white',
                                '&:hover': {
                                  bgcolor: theme.palette.primary.dark,
                                }
                              }}
                            />
                          )}
                          
                          <Tooltip title={expandedCards[index] ? "Collapse" : "Expand"}>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => toggleCardExpansion(index)}
                              sx={{
                                ml: 1,
                                bgcolor: 'rgba(33, 150, 243, 0.08)',
                                '&:hover': {
                                  bgcolor: 'rgba(33, 150, 243, 0.15)',
                                }
                              }}
                            >
                              {expandedCards[index] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    )}
                    
                    {/* Expanded content */}
                    {expandedCards[index] && (
                      <Box sx={{ mt: 2 }}>
                        <Divider sx={{ my: 2 }} />
                        
                        {business.description && (
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start',
                            mb: 2
                          }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1 }}
                              >
                                {business.description}
                              </Typography>
                              
                              {business.aboutUs && (
                                <Box sx={{ mb: 3 }}>
                                  <Typography variant="body2">
                                    {business.aboutUs.substring(0, 500)}
                                    {business.aboutUs.length > 500 && (
                                      <Link 
                                        component="span" 
                                        onClick={() => handleOpenAboutUsModal(business.name, business.aboutUs!)}
                                        sx={{ 
                                          cursor: 'pointer',
                                          ml: 0.5,
                                          color: 'primary.main',
                                          fontWeight: 500,
                                          '&:hover': { textDecoration: 'underline' }
                                        }}
                                      >
                                        more...
                                      </Link>
                                    )}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            
                            {onFindContacts && (
                              <Tooltip title="Refresh business data">
                                <IconButton
                                  color="primary"
                                  onClick={() => onFindContacts && onFindContacts(index)}
                                  disabled={business.isLoadingContacts || !onFindContacts}
                                  size="small"
                                  sx={{
                                    ml: 1,
                                    '& .MuiSvgIcon-root': {
                                      animation: business.isLoadingContacts ? `${spin} 1.5s linear infinite` : 'none'
                                    }
                                  }}
                                >
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                        
                        {!business.description && onReloadBusiness && (
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'flex-end', 
                            mb: 2
                          }}>
                            <Tooltip title="Refresh business data">
                              <IconButton
                                color="primary"
                                onClick={() => onFindContacts && onFindContacts(index)}
                                disabled={business.isLoadingContacts || !onFindContacts}
                                size="small"
                                sx={{
                                  '& .MuiSvgIcon-root': {
                                    animation: business.isLoadingContacts ? `${spin} 1.5s linear infinite` : 'none'
                                  }
                                }}
                              >
                                <RefreshIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                          {business.website && (
                            <Chip
                              icon={<LinkIcon />}
                              label="Website"
                              component="a"
                              href={business.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              clickable
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                              size={isMobile ? "small" : "medium"}
                            />
                          )}

                          {(business as { email?: string }).email && (
                            <Chip
                              icon={<EmailIcon />}
                              label={isMobile ? "Email" : (business as { email?: string }).email}
                              component="a"
                              href={`mailto:${(business as { email?: string }).email}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              clickable
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                              size={isMobile ? "small" : "medium"}
                            />
                          )}

                          {business.phone && (
                            <Chip
                              icon={<PhoneIcon />}
                              label={isMobile ? "Phone" : business.phone}
                              color="secondary"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                              size={isMobile ? "small" : "medium"}
                            />
                          )}

                          {business.businessSocialLinks?.map((link, i) => (
                            <Chip
                              key={i}
                              icon={getSocialIcon(link)}
                              label={getSocialName(link)}
                              component="a"
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              clickable
                              color="default"
                              variant="outlined"
                              sx={{
                                fontWeight: 500,
                                '& .MuiChip-icon': {
                                  color: 'primary.main',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }
                              }}
                              size={isMobile ? "small" : "medium"}
                            />
                          ))}
                        </Box>
                        
                        {/* Contacts section - same as full view */}
                        {business.teamMembers && business.teamMembers.length > 0 ? (
                          <>
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  color: 'primary.main'
                                }}
                              >
                                <ContactPageIcon sx={{ color: 'rgba(33, 150, 243, 0.7)' }} />
                                Contacts
                              </Typography>
                            </Box>
                            <Grid container spacing={isMobile ? 1 : 2}>
                              {business.teamMembers.map((member, i) => (
                                <Grid item xs={12} md={6} key={i}>
                                  <Card
                                    variant="outlined"
                                    sx={{
                                      p: isMobile ? 1 : 2,
                                      height: '100%',
                                      borderRadius: 2,
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                        borderColor: theme.palette.primary.main
                                      }
                                    }}
                                  >
                                    {/* Contact card content - same as full view */}
                                    <Box sx={{ display: 'flex', gap: isMobile ? 1 : 2, alignItems: 'flex-start' }}>
                                      <Avatar
                                        sx={{
                                          bgcolor: 'rgba(33, 150, 243, 0.15)',
                                          color: 'primary.main',
                                          width: isMobile ? 40 : 56,
                                          height: isMobile ? 40 : 56
                                        }}
                                      >
                                        <PersonIcon />
                                      </Avatar>
                                      <Box>
                                        <Typography
                                          variant={isMobile ? "subtitle1" : "h6"}
                                          sx={{
                                            fontWeight: 600,
                                            fontSize: isMobile ? '0.95rem' : '1.1rem',
                                            lineHeight: 1.2,
                                            mb: 0.5
                                          }}
                                        >
                                          {member.name}
                                        </Typography>

                                        {member.role && (
                                          <Chip
                                            label={member.role}
                                            size="small"
                                            sx={{
                                              mb: 1.5,
                                              fontWeight: 500,
                                              bgcolor: 'rgba(33, 150, 243, 0.1)',
                                              color: 'primary.main',
                                              border: '1px solid rgba(33, 150, 243, 0.2)'
                                            }}
                                          />
                                        )}

                                        <Stack spacing={1} sx={{ mt: 1 }}>
                                          {member.email && (
                                            <Box sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1,
                                              color: 'text.secondary'
                                            }}>
                                              <EmailIcon fontSize="small" color="action" />
                                              <Link
                                                href={`mailto:${member.email}`}
                                                underline="hover"
                                                sx={{
                                                  color: theme.palette.primary.main,
                                                  fontWeight: 500,
                                                  fontSize: isMobile ? '0.85rem' : 'inherit'
                                                }}
                                              >
                                                {isMobile ? 'Email' : member.email}
                                              </Link>
                                            </Box>
                                          )}

                                          {member.phone && (
                                            <Box sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1,
                                              color: 'text.secondary'
                                            }}>
                                              <PhoneIcon fontSize="small" color="action" />
                                              <Link
                                                href={`tel:${member.phone}`}
                                                underline="hover"
                                                sx={{
                                                  color: theme.palette.primary.main,
                                                  fontWeight: 500,
                                                  fontSize: isMobile ? '0.85rem' : 'inherit'
                                                }}
                                              >
                                                {isMobile ? 'Phone' : member.phone}
                                              </Link>
                                            </Box>
                                          )}
                                        </Stack>

                                        {member.bio && (
                                          <Box sx={{ mt: 2 }}>
                                            <Tooltip title="View Bio">
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<DescriptionIcon />}
                                                onClick={() => handleOpenBioModal(member.name, member.bio!)}
                                                sx={{
                                                  fontSize: '0.75rem',
                                                  textTransform: 'none'
                                                }}
                                              >
                                                View Bio
                                              </Button>
                                            </Tooltip>
                                          </Box>
                                        )}

                                        {member.socialLinks && member.socialLinks.length > 0 && (
                                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                                            {member.socialLinks.map((link, j) => (
                                              <Chip
                                                key={j}
                                                size="small"
                                                icon={getSocialIcon(link)}
                                                label={getSocialName(link)}
                                                component="a"
                                                href={link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                clickable
                                                variant="outlined"
                                                sx={{
                                                  fontSize: '0.75rem',
                                                  '& .MuiChip-icon': {
                                                    color: 'primary.main',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                  }
                                                }}
                                              />
                                            ))}
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>
                          </>
                        ) : business.isLoadingContacts && (!business.teamMembers || business.teamMembers.length === 0) ? (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2, 
                            my: 3,
                            p: 3,
                            bgcolor: 'rgba(0, 0, 0, 0.02)',
                            borderRadius: 2
                          }}>
                            <CircularProgress size={24} color="primary" />
                            <Typography>Finding contacts...</Typography>
                          </Box>
                        ) : business.contactsError ? (
                          <Alert
                            severity="error"
                            sx={{
                              mt: 2,
                              borderRadius: 2
                            }}
                          >
                            <AlertTitle>Error Finding Contacts</AlertTitle>
                            {business.contactsError}
                          </Alert>
                        ) : business.contactsMessage ? (
                          <Alert
                            severity="info"
                            sx={{
                              mt: 2,
                              borderRadius: 2
                            }}
                          >
                            <AlertTitle>No Contacts Found</AlertTitle>
                            {business.contactsMessage}
                          </Alert>
                        ) : null}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ) : (
                // Full View (original card)
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    overflow: 'hidden'
                  }}
                >
                  <CardContent sx={{ pb: 1, px: isMobile ? 2 : 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {/* Relevancy score circle (full view) */}
                        <Box sx={{ 
                          width: 44, 
                          height: 44, 
                          borderRadius: '50%', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          bgcolor: business.relevancyScore !== undefined 
                            ? (business.relevancyScore > 70 ? 'rgba(76, 175, 80, 0.15)' : 
                              business.relevancyScore > 40 ? 'rgba(255, 152, 0, 0.15)' : 'rgba(244, 67, 54, 0.15)')
                            : 'rgba(0, 0, 0, 0.05)',
                          color: business.relevancyScore !== undefined
                            ? (business.relevancyScore > 70 ? 'success.dark' : 
                              business.relevancyScore > 40 ? 'warning.dark' : 'error.dark')
                            : 'text.secondary',
                          fontWeight: 'bold',
                          fontSize: '0.9rem',
                          flexShrink: 0,
                          border: 1,
                          borderColor: business.relevancyScore !== undefined
                            ? (business.relevancyScore > 70 ? 'rgba(76, 175, 80, 0.3)' : 
                              business.relevancyScore > 40 ? 'rgba(255, 152, 0, 0.3)' : 'rgba(244, 67, 54, 0.3)')
                            : 'rgba(0, 0, 0, 0.1)'
                        }}>
                          {business.relevancyScore !== undefined ? `${business.relevancyScore}%` : '—'}
                        </Box>
                        
                        <Typography
                          variant={isMobile ? "h6" : "h5"}
                          gutterBottom
                          sx={{
                            fontWeight: 600,
                            color: 'rgba(33, 150, 243, 0.8)'
                          }}
                        >
                          {business.name}
                        </Typography>
                      </Box>
                      
                      {onReloadBusiness && (
                        <IconButton
                          color="primary"
                          onClick={() => onReloadBusiness(index)}
                          disabled={business.isLoadingBusiness}
                          size="small"
                          sx={{
                            '& .MuiSvgIcon-root': {
                              animation: business.isLoadingBusiness ? `${spin} 1.5s linear infinite` : 'none'
                            }
                          }}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                      sx={{ mb: 2 }}
                    >
                      {business.address}
                    </Typography>

                    {business.description && (
                      <Typography
                        variant="body2"
                        paragraph
                        sx={{ mb: 2 }}
                      >
                        {business.description}
                      </Typography>
                    )}

                    {business.aboutUs && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2">
                          {business.aboutUs.substring(0, 500)}
                          {business.aboutUs.length > 500 && (
                            <Link 
                              component="span" 
                              onClick={() => handleOpenAboutUsModal(business.name, business.aboutUs!)}
                              sx={{ 
                                cursor: 'pointer',
                                ml: 0.5,
                                color: 'primary.main',
                                fontWeight: 500,
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              more...
                            </Link>
                          )}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                      {business.website && (
                        <Chip
                          icon={<LinkIcon />}
                          label="Website"
                          component="a"
                          href={business.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          clickable
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                          size={isMobile ? "small" : "medium"}
                        />
                      )}

                      {(business as { email?: string }).email && (
                        <Chip
                          icon={<EmailIcon />}
                          label={isMobile ? "Email" : (business as { email?: string }).email}
                          component="a"
                          href={`mailto:${(business as { email?: string }).email}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          clickable
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                          size={isMobile ? "small" : "medium"}
                        />
                      )}

                      {business.phone && (
                        <Chip
                          icon={<PhoneIcon />}
                          label={isMobile ? "Phone" : business.phone}
                          color="secondary"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                          size={isMobile ? "small" : "medium"}
                        />
                      )}

                      {business.businessSocialLinks?.map((link, i) => (
                        <Chip
                          key={i}
                          icon={getSocialIcon(link)}
                          label={getSocialName(link)}
                          component="a"
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          clickable
                          color="default"
                          variant="outlined"
                          sx={{
                            fontWeight: 500,
                            '& .MuiChip-icon': {
                              color: 'primary.main',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }
                          }}
                          size={isMobile ? "small" : "medium"}
                        />
                      ))}
                    </Box>

                    {business.teamMembers && business.teamMembers.length > 0 ? (
                      <>
                        <Divider sx={{ my: 3 }} />
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              color: 'primary.main'
                            }}
                          >
                            <ContactPageIcon sx={{ color: 'rgba(33, 150, 243, 0.7)' }} />
                            Contacts
                          </Typography>
                        </Box>
                        <Grid container spacing={isMobile ? 1 : 2}>
                          {business.teamMembers.map((member, i) => (
                            <Grid item xs={12} md={6} key={i}>
                              <Card
                                variant="outlined"
                                sx={{
                                  p: isMobile ? 1 : 2,
                                  height: '100%',
                                  borderRadius: 2,
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                    borderColor: theme.palette.primary.main
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', gap: isMobile ? 1 : 2, alignItems: 'flex-start' }}>
                                  <Avatar
                                    sx={{
                                      bgcolor: 'rgba(33, 150, 243, 0.15)',
                                      color: 'primary.main',
                                      width: isMobile ? 40 : 56,
                                      height: isMobile ? 40 : 56
                                    }}
                                  >
                                    <PersonIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography
                                      variant={isMobile ? "subtitle1" : "h6"}
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: isMobile ? '0.95rem' : '1.1rem',
                                        lineHeight: 1.2,
                                        mb: 0.5
                                      }}
                                    >
                                      {member.name}
                                    </Typography>

                                    {member.role && (
                                      <Chip
                                        label={member.role}
                                        size="small"
                                        sx={{
                                          mb: 1.5,
                                          fontWeight: 500,
                                          bgcolor: 'rgba(33, 150, 243, 0.1)',
                                          color: 'primary.main',
                                          border: '1px solid rgba(33, 150, 243, 0.2)'
                                        }}
                                      />
                                    )}

                                    <Stack spacing={1} sx={{ mt: 1 }}>
                                      {member.email && (
                                        <Box sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          color: 'text.secondary'
                                        }}>
                                          <EmailIcon fontSize="small" color="action" />
                                          <Link
                                            href={`mailto:${member.email}`}
                                            underline="hover"
                                            sx={{
                                              color: theme.palette.primary.main,
                                              fontWeight: 500,
                                              fontSize: isMobile ? '0.85rem' : 'inherit'
                                            }}
                                          >
                                            {isMobile ? 'Email' : member.email}
                                          </Link>
                                        </Box>
                                      )}

                                      {member.phone && (
                                        <Box sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          color: 'text.secondary'
                                        }}>
                                          <PhoneIcon fontSize="small" color="action" />
                                          <Link
                                            href={`tel:${member.phone}`}
                                            underline="hover"
                                            sx={{
                                              color: theme.palette.primary.main,
                                              fontWeight: 500,
                                              fontSize: isMobile ? '0.85rem' : 'inherit'
                                            }}
                                          >
                                            {isMobile ? 'Phone' : member.phone}
                                          </Link>
                                        </Box>
                                      )}

                                    </Stack>

                                    {member.bio && (
                                      <Box sx={{ mt: 2 }}>
                                        <Tooltip title="View Bio">
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<DescriptionIcon />}
                                            onClick={() => handleOpenBioModal(member.name, member.bio!)}
                                            sx={{
                                              fontSize: '0.75rem',
                                              textTransform: 'none'
                                            }}
                                          >
                                            View Bio
                                          </Button>
                                        </Tooltip>
                                      </Box>
                                    )}

                                    {member.socialLinks && member.socialLinks.length > 0 && (
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                                        {member.socialLinks.map((link, j) => (
                                          <Chip
                                            key={j}
                                            size="small"
                                            icon={getSocialIcon(link)}
                                            label={getSocialName(link)}
                                            component="a"
                                            href={link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            clickable
                                            variant="outlined"
                                            sx={{
                                              fontSize: '0.75rem',
                                              '& .MuiChip-icon': {
                                                color: 'primary.main',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                              }
                                            }}
                                          />
                                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </>
                    ) : business.isLoadingContacts && (!business.teamMembers || business.teamMembers.length === 0) ? (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2, 
                        my: 3,
                        p: 3,
                        bgcolor: 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 2
                      }}>
                        <CircularProgress size={24} color="primary" />
                        <Typography>Finding contacts...</Typography>
                      </Box>
                    ) : business.contactsError ? (
                      <Alert
                        severity="error"
                        sx={{
                          mt: 2,
                          borderRadius: 2
                        }}
                      >
                        <AlertTitle>Error Finding Contacts</AlertTitle>
                        {business.contactsError}
                      </Alert>
                    ) : business.contactsMessage ? (
                      <Alert
                        severity="info"
                        sx={{
                          mt: 2,
                          borderRadius: 2
                        }}
                      >
                        <AlertTitle>No Contacts Found</AlertTitle>
                        {business.contactsMessage}
                      </Alert>
                    ) : null}

                    {!business.isLoadingContacts &&
                      !business.teamMembers &&
                      !business.contactsError &&
                      !business.contactsMessage &&
                      business.website &&
                      business.website !== '#' &&
                      onFindContacts && (
                        <>
                          <Divider sx={{ my: 3 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                color: 'primary.main'
                              }}
                            >
                              <ContactPageIcon sx={{ color: 'rgba(33, 150, 243, 0.7)' }} />
                              Contacts
                            </Typography>
                          </Box>
                        </>
                      )}
                  </CardContent>
                </Card>
              )}
            </Grid>
          ))}
        </Grid>

        {/* Load More Button */}
        {results.length > 0 && onLoadMore && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
            {isLoadingMore && formData?.maxResults && (
              <Box sx={{ width: '100%', maxWidth: 400, mb: 2 }}>
                <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
                  {results.length >= formData.maxResults 
                    ? `Loading additional results beyond limit of ${formData.maxResults}...` 
                    : `Auto-loading results: ${results.length} of ${formData.maxResults} (${Math.round((results.length / formData.maxResults) * 100)}%)`
                  }
                </Typography>
                <LinearProgress 
                  variant="indeterminate" 
                  sx={{ 
                    height: '8px', 
                    borderRadius: '4px',
                    '& .MuiLinearProgress-bar': {
                      transition: 'transform 0.4s linear'
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 1, display: 'block' }}>
                  Finding contacts and fetching more businesses in parallel...
                </Typography>
              </Box>
            )}
            
            {noMoreResults && !isLoadingMore && (
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 2, 
                  width: '100%', 
                  maxWidth: 400,
                  borderRadius: '8px'
                }}
              >
                <AlertTitle>No More Results</AlertTitle>
                No additional businesses matching your criteria were found. Try modifying your search parameters for different results.
              </Alert>
            )}
            
            <Button
              variant="contained"
              color="primary"
              startIcon={isLoadingMore ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
              onClick={onLoadMore}
              disabled={!!isLoadingMore || noMoreResults}
              sx={{ px: 4, py: 1 }}
              fullWidth={isMobile}
            >
              {isLoadingMore 
                ? `Loading More Results...` 
                : noMoreResults
                  ? `No More Results Available (${results.length})`
                  : formData?.maxResults && results.length >= formData.maxResults
                    ? `Load Additional Results (${results.length})`
                    : 'Load More Results'
              }
            </Button>
          </Box>
        )}
      </Paper>

      {/* Bio Modal */}
      <Modal
        open={bioModalOpen}
        onClose={handleCloseBioModal}
        aria-labelledby="bio-modal-title"
        aria-describedby="bio-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          maxHeight: '80vh',
          overflow: 'auto'
        }}>
          {selectedBio && (
            <>
              <Typography id="bio-modal-title" variant="h6" component="h2" gutterBottom>
                {selectedBio.name}'s Bio
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography id="bio-modal-description" sx={{ mt: 2 }}>
                {selectedBio.bio}
              </Typography>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={handleCloseBioModal} variant="outlined">
                  Close
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {/* About Us Modal */}
      <Modal
        open={aboutUsModalOpen}
        onClose={handleCloseAboutUsModal}
        aria-labelledby="about-us-modal-title"
        aria-describedby="about-us-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          maxHeight: '80vh',
          overflow: 'auto'
        }}>
          {selectedAboutUs && (
            <>
              <Typography id="about-us-modal-title" variant="h6" component="h2" gutterBottom>
                About {selectedAboutUs.name}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography id="about-us-modal-description" sx={{ mt: 2 }}>
                {selectedAboutUs.aboutUs}
              </Typography>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={handleCloseAboutUsModal} variant="outlined">
                  Close
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default ResultsDisplay; 